import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { Flex, ResponsiveBreak, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import useEmblaCarousel from 'embla-carousel-react';
import type React from 'react';
import styled from 'styled-components';
import { SliderDotButton, SliderDotButtonWrapper } from '../shared/slider/SliderDotButton';
import { useSliderDotButton } from '../shared/slider/useSliderDotButton';
import { CheckMarkCircleIcon, PetLandingPageType } from './shared';
const PricingWrapper = styled(Section)`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 16px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

const PackagesWrapper = styled(Flex).attrs({ justifyContent: 'space-between' })`
  display: none;
  width: 100%;

  ${MEDIA_QUERIES.tabletAndDesktop`
        display: flex;
        max-width: 1140px;
        gap: 18px;
    `}
`;

const PackageHeading = styled(Flex).attrs({
  flexDirection: 'column',
  alignItems: 'center',
})`
  ${font('main', { fontSize: '1.8rem', lineHeight: '1.5' })};
  margin-bottom: 24px;

  > span {
    text-align: center;
  }

  > span:last-child {
    font-size: 1.6rem;
  }
`;

const PackageItems = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const PackageListItem = styled(Flex).attrs({ as: 'li', alignItems: 'center' })`
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.5' })};
  gap: 8px;
  margin-bottom: 12px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 14px;
    `}
`;

const CardWrapperForSlider = styled(Flex)`
  padding: 0 8px;
  width: 340px;
  height: 420px;
`;

const PackageCard = styled(Flex).attrs({ flexDirection: 'column' })`
  background-color: ${themedColor('white')};
  padding: 24px 24px 12px 24px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  height: 100%;
  border-radius: 12px;
  flex-grow: 1;
  border: 1px solid ${themedColor('glass')};

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin: 0;
        width: 340px;
        height: auto;
        padding: 24px 50px;
        box-shadow: 0px 15px 54px rgba(0, 0, 0, 0.06);
    `}
`;

const CarouselContainer = styled(Flex)`
  display: flex;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}
`;

const basicPackageItems = ['Wellness Exam', 'Vaccines', 'Wellness Testing', 'Medical Advice Chat'];

const packages = [
  {
    name: {
      [PetLandingPageType.Dog]: 'Preventative',
      [PetLandingPageType.Cat]: 'Preventative',
      [PetLandingPageType.PetInsurance]: 'Preventative for Adult Pets',
    },
    price: {
      [PetLandingPageType.Dog]: '15',
      [PetLandingPageType.Cat]: '10',
      [PetLandingPageType.PetInsurance]: '10',
    },
    includedItems: basicPackageItems,
  },
  {
    name: {
      [PetLandingPageType.Dog]: 'Preventative+ for Adult Dogs',
      [PetLandingPageType.Cat]: 'Preventative+ for Adult Cats',
      [PetLandingPageType.PetInsurance]: 'Preventative+ for Adult Pets',
    },
    price: {
      [PetLandingPageType.Dog]: '22',
      [PetLandingPageType.Cat]: '15',
      [PetLandingPageType.PetInsurance]: '15',
    },
    includedItems: [...basicPackageItems, 'Flea/Tick or Heartworm Medication', 'Routine Dental Cleaning'],
  },
  {
    name: {
      [PetLandingPageType.Dog]: 'Preventative for Puppies',
      [PetLandingPageType.Cat]: 'Preventative for Kittens',
      [PetLandingPageType.PetInsurance]: 'Preventative for Puppies/Kittens',
    },
    price: {
      [PetLandingPageType.Dog]: '40',
      [PetLandingPageType.Cat]: '31',
      [PetLandingPageType.PetInsurance]: '31',
    },
    includedItems: [...basicPackageItems, 'Spay/Neuter', 'Microchip', 'Flea/Tick or Heartworm Medication'],
  },
];

interface PricingProps {
  petLandingPageType: PetLandingPageType;
}

const Carousel: React.FC<{
  petLandingPageType: PetLandingPageType;
}> = ({ petLandingPageType }) => {
  const [emblaRef, slideApi] = useEmblaCarousel({
    containScroll: false,
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useSliderDotButton(slideApi);

  return (
    <CarouselContainer overflow="hidden" ref={emblaRef} flexDirection="column">
      <Flex gap="20px" mb="12px">
        {packages.map(({ name, price, includedItems }) => (
          <CardWrapperForSlider key={name[petLandingPageType]}>
            <PackageCard>
              <PackageHeading>
                <span style={{ fontWeight: 700 }}>{name[petLandingPageType]}</span>
                <span>{`from +$${price[petLandingPageType]}/Mo`}</span>
              </PackageHeading>
              <PackageItems>
                {includedItems.map(item => (
                  <PackageListItem key={item}>
                    <CheckMarkCircleIcon />
                    {item}
                  </PackageListItem>
                ))}
              </PackageItems>
            </PackageCard>
          </CardWrapperForSlider>
        ))}
      </Flex>

      <SliderDotButtonWrapper>
        {scrollSnaps.map((_, index) => (
          <SliderDotButton key={index} onClick={() => onDotButtonClick(index)} isActive={index === selectedIndex} />
        ))}
      </SliderDotButtonWrapper>
    </CarouselContainer>
  );
};

const Pricing: React.FC<PricingProps> = ({ petLandingPageType }) => {
  return (
    <PricingWrapper justifyContent="center" py={[40, 70]} pb={[40, 90]}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>
          {petLandingPageType === PetLandingPageType.Cat ? 'Start Saving on' : 'Huge Savings on'}
          <ResponsiveBreak display={['block', 'block', 'inline']} />
          {petLandingPageType === PetLandingPageType.Cat ? 'Tests, Vaccines, &' : 'Tests, Vaccines, and'}
          <ResponsiveBreak display={['block', 'block', 'inline']} />
          Routine Care
        </Heading>
        <SubHeading>
          <>
            Preventative packages cover
            <ResponsiveBreak display={['block', 'inline']} />
            vet care you’re probably
            <ResponsiveBreak display={['block', 'inline']} />
            already paying for
          </>
        </SubHeading>
      </Flex>
      <PackagesWrapper>
        {packages.map(({ name, price, includedItems }) => (
          <PackageCard key={name[petLandingPageType]}>
            <PackageHeading>
              <span style={{ fontWeight: 700 }}>{name[petLandingPageType]}</span>
              <span>{`From +$${price[petLandingPageType]}/Mo`}</span>
            </PackageHeading>
            <PackageItems>
              {includedItems.map(item => (
                <PackageListItem key={item}>
                  <CheckMarkCircleIcon />
                  {item}
                </PackageListItem>
              ))}
            </PackageItems>
          </PackageCard>
        ))}
      </PackagesWrapper>
      <Carousel petLandingPageType={petLandingPageType} />
    </PricingWrapper>
  );
};

export default Pricing;
